import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Archive, EyeOff, Pencil, Pin, Search, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useActivity } from "@/lib/running";
import { tempChats, type TempChat } from "@/lib/temp";
import type { Chat } from "@/lib/types";

function groupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const day = 86_400_000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return "Today";
  if (t >= startOfToday - day) return "Yesterday";
  if (t >= startOfToday - 7 * day) return "Previous 7 days";
  if (t >= startOfToday - 30 * day) return "Previous 30 days";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long" });
}

function useTempChats() {
  const [list, setList] = useState<TempChat[]>([]);
  useEffect(() => {
    const load = () => tempChats.list().then(setList).catch(() => setList([]));
    load();
    window.addEventListener("temp-chats-changed", load);
    return () => window.removeEventListener("temp-chats-changed", load);
  }, []);
  return list;
}

/** A dot after the title while the chat's reply is being written, amber when it needs an approval. */
function ActivityDot({ id }: { id: string }) {
  const activity = useActivity(id);
  if (!activity) return null;
  const approval = activity === "approval";
  return (
    <span
      className="relative flex size-2 shrink-0"
      title={approval ? "Waiting for your approval" : "Writing a reply"}
      aria-label={approval ? "Waiting for your approval" : "Writing a reply"}
      role="status"
    >
      {!approval && <span className="absolute inline-flex size-full animate-ping rounded-full bg-indigo-400 opacity-75" />}
      <span className={clsx("relative inline-flex size-2 rounded-full", approval ? "bg-amber-500" : "bg-indigo-500")} />
    </span>
  );
}

const itemClass = (active: boolean) =>
  clsx(
    "group flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm",
    active ? "bg-zinc-200/80 dark:bg-zinc-800" : "hover:bg-zinc-200/50 dark:hover:bg-zinc-800/60",
  );

function ChatItem({ chat }: { chat: Chat }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(chat.title);
  const patch = useMutation({
    mutationFn: (body: Partial<Chat>) => api.patch(`/api/chats/${chat.id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chats", "list"] }),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/chats/${chat.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chats", "list"] });
      if (location.pathname === `/c/${chat.id}`) navigate("/");
    },
  });

  if (renaming)
    return (
      <form
        className="px-1 py-0.5"
        onSubmit={(e) => {
          e.preventDefault();
          setRenaming(false);
          if (title.trim() && title !== chat.title) patch.mutate({ title: title.trim() });
        }}
      >
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => setRenaming(false)}
          onKeyDown={(e) => e.key === "Escape" && setRenaming(false)}
          className="w-full rounded-md border border-indigo-500 bg-white px-2 py-1 text-sm outline-none dark:bg-zinc-900"
        />
      </form>
    );

  const btn = "rounded p-0.5 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100";
  return (
    <NavLink to={`/c/${chat.id}`} className={({ isActive }) => itemClass(isActive)} title={chat.title}>
      {chat.pinned && <Pin size={12} className="shrink-0 text-zinc-400" />}
      <span className="min-w-0 flex-1 truncate">{chat.title}</span>
      <ActivityDot id={chat.id} />
      <span className="hidden shrink-0 items-center gap-0.5 group-hover:flex [@media(hover:none)]:flex" onClick={(e) => e.preventDefault()}>
        <button className={btn} onClick={() => patch.mutate({ pinned: !chat.pinned })} title={chat.pinned ? "Unpin" : "Pin"}>
          <Pin size={13} />
        </button>
        <button
          className={btn}
          onClick={() => {
            setTitle(chat.title);
            setRenaming(true);
          }}
          title="Rename"
        >
          <Pencil size={13} />
        </button>
        <button className={btn} onClick={() => patch.mutate({ archived: true })} title="Archive">
          <Archive size={13} />
        </button>
        <button className={btn} onClick={() => confirm(`Delete "${chat.title}"?`) && remove.mutate()} title="Delete">
          <Trash2 size={13} />
        </button>
      </span>
    </NavLink>
  );
}

export default function ChatList() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const chats = useQuery({
    queryKey: ["chats", "list", debounced],
    queryFn: () => api.get<Chat[]>(`/api/chats${debounced ? `?q=${encodeURIComponent(debounced)}` : ""}`),
  });
  const temps = useTempChats();

  const groups = new Map<string, Chat[]>();
  for (const c of chats.data ?? []) {
    const label = c.pinned ? "Pinned" : groupLabel(c.updated_at);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(c);
  }

  return (
    <div className="space-y-3 pb-4">
      <div className="relative px-0.5">
        <Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search chats"
          className="h-8 w-full rounded-lg bg-zinc-200/60 pr-7 pl-8 text-sm outline-none placeholder:text-zinc-500 focus:ring-2 focus:ring-indigo-500/30 dark:bg-zinc-800"
        />
        {q && (
          <button className="absolute top-1/2 right-2 -translate-y-1/2 text-zinc-400" onClick={() => setQ("")} aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>
      {!debounced && temps.length > 0 && (
        <div>
          <p className="px-2.5 pb-1 text-xs font-medium text-zinc-500">Temporary (this browser)</p>
          {temps.map((t) => (
            <NavLink key={t.id} to={`/temp/${t.id}`} className={({ isActive }) => itemClass(isActive)} title={t.title}>
              <EyeOff size={12} className="shrink-0 text-amber-600" />
              <span className="min-w-0 flex-1 truncate">{t.title || "Temporary chat"}</span>
              <ActivityDot id={t.id} />
            </NavLink>
          ))}
        </div>
      )}
      {[...groups].map(([label, list]) => (
        <div key={label}>
          <p className="px-2.5 pb-1 text-xs font-medium text-zinc-500">{label}</p>
          {list.map((c) => (
            <ChatItem key={c.id} chat={c} />
          ))}
        </div>
      ))}
      {chats.data?.length === 0 && <p className="px-2.5 text-sm text-zinc-500">{debounced ? "No matches" : "No chats yet"}</p>}
    </div>
  );
}
